// @ts-nocheck

import './style.css'
import * as THREE from 'three'
import glslify from 'glslify'

import { mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils'
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { PathTracingSceneWorker } from './workers/PathTracingSceneWorker'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass'
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass'
import { GlitchPass } from 'three/examples/jsm/postprocessing/GlitchPass'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'
import {
  PhysicalCamera,
  PathTracingRenderer,
  PhysicalPathTracingMaterial,
} from 'three-gpu-pathtracer'
import {
  clamp,
  getPointsDist,
  map,
  randomFloat,
  randomInt,
  randomNormal,
  TO_DEGREE,
  TO_RAD,
} from './math'

import neonFs from './shaders/neon-fs.glsl'

import characters from 'url:../public/characters.glb'

import envUrl from 'url:../public/pedestrian_overpass_1k.hdr'

import { DitherShader } from './shaders/dither'
import { ScratchShader } from './shaders/scratch'
import rawVs from './shaders/default-raw-vs.glsl'
import rawFs from './shaders/default-raw-fs.glsl'
import noiseThree from './shaders/noise-three.glsl'
import maquetteFs from './shaders/maquette.glsl'

/**
 *
 */
;(async () => {
  let windowHeight
  let windowWidth
  // let isAuto = true
  let isRendering = true
  let isFirstStop = true
  let isCompletedRendering = false

  const isPortrait = window.fxrand() < 0.5
  const isBAW = window.fxrand() < 0.06
  const isOverlayed = isBAW && window.fxrand() < 0.1
  const isDithering = window.fxrand() < 0.2
  const zoomLevel = randomFloat(
    isPortrait ? 0.23 : 0.15,
    isPortrait ? 0.4 : 0.3,
    16
  )
  const fStop = randomFloat(0.1, 1.5, 3)
  const envIntensity = randomFloat(5, 8, 2)

  const isOrtho = true
  const orthoWidth = 2
  let material
  let isPreloading = true

  const getZoomLevel = () => {
    if ((isPortrait && zoomLevel < 0.29) || (!isPortrait && zoomLevel < 0.21))
      return 'low'
    else if (
      (isPortrait && zoomLevel < 0.35) ||
      (!isPortrait && zoomLevel < 0.26)
    )
      return 'medium'
    else return 'high'
  }

  const COLORS = [
    ['#658264', '#7c8687', '#ac6e5f', '#ba9b62'],
    ['#05bfd6', '#e1a813', '#3b7658', '#d86351'],
    // ['#fe2cff', '#4117ff', '#fff428', '#00b47f'],
    // ['#06ae95', '#016624', '#f282a8', '#df001b'],
    ['#8e6934', '#f8f3ef', '#577180', '#a7141c'],
    ['#3a736a', '#e0645c', '#c2baaf', '#336d61'],
    ['#1c4154', '#346f8d', '#ccd5da', '#274c5f'],
    ['#dfd4a7', '#f6dc4b', '#d4d4c8', '#363531'],
  ]

  const rdmIndexColor = randomInt(0, COLORS.length - 1)

  window.$fxhashFeatures = {
    Portrait: isPortrait,
    'Zoom level': getZoomLevel(),
    FStop: fStop,
    'Environment intensity': envIntensity,
    'Color mode': rdmIndexColor,
    Dithering: isDithering,
    'Black & White': isBAW,
    Overlayed: isOverlayed,
  }

  if (isPortrait) {
    windowHeight = window.innerHeight
    windowWidth = (windowHeight / 4) * 3
  } else {
    windowWidth = window.innerWidth
    windowHeight = (windowWidth / 4) * 3
  }

  const pixelRatio = Math.min(window.devicePixelRatio, 2)
  let saveNextFrame = false

  const mouse = new THREE.Vector2()
  let bvhInfo: any

  const params = {
    bounces: 3,
    resolutionScale: 1,
    tiles: window.isFxpreview ? 1 : 2,
  }

  // const aspectRatio = windowWidth / windowHeight
  // if (aspectRatio < 0.65) {
  //   params.resolutionScale *= 0.5
  // }

  const CANVAS_SIZE = 1280
  const targetTab = []

  /**
   * Canvas
   */

  const drawLines = (ctx: CanvasRenderingContext2D) => {
    const total = 5

    for (let i = 0; i < total; i++) {
      ctx.moveTo(0, (i * CANVAS_SIZE) / (total - 1))
      ctx.lineTo(CANVAS_SIZE, (i * CANVAS_SIZE) / (total - 1))
      ctx.stroke()
    }

    // ctx.moveTo(0, canvas.height)
    // ctx.lineTo(canvas.width, canvas.height)
    // ctx.stroke()
  }

  const canvasTab = []

  // const rdmIndexColor = randomInt(0, COLORS.length - 1)
  randomInt(0, 1)

  const drawCanvas = () => {
    /* First tex */

    const canvas = document.createElement('canvas')
    canvas.width = CANVAS_SIZE
    canvas.height = CANVAS_SIZE
    const ctx = canvas.getContext('2d')!

    // ctx.strokeStyle = '#000000'
    // ctx.lineWidth = 1

    ctx.fillStyle = COLORS[rdmIndexColor][0]

    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    // drawWindows(ctx)

    canvasTab.push(canvas)

    /* Second tex */

    const canvasB = document.createElement('canvas')
    canvasB.width = CANVAS_SIZE
    canvasB.height = CANVAS_SIZE
    const ctxB = canvasB.getContext('2d')!

    ctxB.fillStyle = COLORS[rdmIndexColor][1]

    ctxB.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    drawLines(ctxB)
    canvasTab.push(canvasB)

    /* Third tex */

    const canvasC = document.createElement('canvas')
    canvasC.width = CANVAS_SIZE
    canvasC.height = CANVAS_SIZE
    const ctxC = canvasC.getContext('2d')!

    ctxC.fillStyle = COLORS[rdmIndexColor][2]

    ctxC.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    drawLines(ctxC)
    canvasTab.push(canvasC)

    /* Fourth tex */

    const canvasD = document.createElement('canvas')
    canvasD.width = CANVAS_SIZE
    canvasD.height = CANVAS_SIZE
    const ctxD = canvasD.getContext('2d')!

    ctxD.fillStyle = COLORS[rdmIndexColor][3]

    ctxD.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    drawLines(ctxD)
    canvasTab.push(canvasD)

    /* Neon tex */

    const canvasE = document.createElement('canvas')
    canvasE.width = 200
    canvasE.height = 600

    canvasTab.push(canvasE)

    /* Overlay canvas */

    // const canvasF = document.createElement('canvas')
    // canvasF.width = windowWidth
    // canvasF.height = windowHeight
    // const ctxF = canvasF.getContext('2d')!

    // canvasTab.push(canvasF)

    /* Background canvas */

    // const canvasG = document.createElement('canvas')
    // canvasG.width = windowWidth
    // canvasG.height = windowHeight
    // const ctxG = canvasG.getContext('2d')!

    // ctxG.fillStyle = '#ff0000'
    // ctxG.fillRect(0, 0, canvasG.width, canvasG.height)

    // canvasTab.push(canvasG)
  }

  const canvasToTexture = () => {
    /**
     * Add material to plane to render target because three gpu needs texture
     * Draw the 2d canvas texture with custom shader on a plane
     */

    const geo = new THREE.PlaneBufferGeometry(2, 2)

    for (let i = 0; i < targetTab.length; i++) {
      const tex = new THREE.CanvasTexture(canvasTab[i])

      material = new THREE.ShaderMaterial({
        vertexShader: rawVs,
        fragmentShader:
          i === 0 ? maquetteFs : i === targetTab.length - 1 ? neonFs : rawFs,
        uniforms: {
          tMap: {
            value: tex,
          },
          uRand: {
            value: window.fxrand(),
          },
          uStrength: {
            value: randomFloat(0.06, 0.1, 3),
          },
          uTime: {
            value: window.fxrand(),
          },
          uCol1: {
            value: randomFloat(0, 1, 3),
          },
          uCol2: {
            value: randomFloat(0, 1, 3),
          },
          uCol3: {
            value: randomFloat(0, 1, 3),
          },
          // uPlacement1: {
          //   value: randomFloat(0.37, 0.4, 2),
          // },
          // uPlacement2: {
          //   value: randomFloat(0, 1),
          // },
          // uPlacement3: {
          //   value: randomFloat(0, 1),
          // },
          // uPlacement4: {
          //   value: randomFloat(0, 1),
          // }
        },
      })

      const plane = new THREE.Mesh(geo, material)

      targetTab[i].sceneTarget.add(plane)
    }

    /* Need to render to texture because three gpu does not support shaderMaterial */

    for (let i = 0; i < targetTab.length; i++) {
      renderer.setRenderTarget(targetTab[i].texTarget)
      renderer.render(targetTab[i].sceneTarget, orthoCamera)
    }

    renderer.setRenderTarget(null)
  }

  drawCanvas()

  /**
   * Renderer
   */

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
  })
  renderer.toneMapping = THREE.ACESFilmicToneMapping

  renderer.setPixelRatio(pixelRatio * params.resolutionScale)
  renderer.setSize(windowWidth, windowHeight, false)

  renderer.domElement.id = 'three-canvas'
  document.body.appendChild(renderer.domElement)

  /**
   * Camera
   */

  const camera = new PhysicalCamera(60, windowWidth / windowHeight, 0.025, 500)
  camera.position.set(0, 10, 0)
  camera.fStop = fStop

  camera.updateProjectionMatrix()

  const orthoHeight = orthoWidth / (windowWidth / windowHeight)
  const orthoCameraMain = new THREE.OrthographicCamera(
    orthoWidth / -2,
    orthoWidth / 2,
    orthoHeight / 2,
    orthoHeight / -2,
    0,
    500
  )
  orthoCameraMain.position.set(
    randomFloat(-2.9679351363494, 0.5758915360605563, 16),
    randomFloat(8.201901295071448, 10.094991576338769, 16),
    1.0752729264959733
  )
  orthoCameraMain.zoom = zoomLevel
  orthoCameraMain.fStop = fStop

  orthoCameraMain.updateProjectionMatrix()

  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

  /**
   * Ray Tracing renderer
   */

  const rtRenderer = new PathTracingRenderer(renderer)
  rtRenderer.setSize(
    windowWidth * params.resolutionScale,
    windowHeight * params.resolutionScale
  )
  rtRenderer.camera = isOrtho ? orthoCameraMain : camera
  rtRenderer.tiles.set(params.tiles, params.tiles)
  rtRenderer.material = new PhysicalPathTracingMaterial()
  rtRenderer.material.setDefine('FEATURE_GRADIENT_BG', 1)
  // rtRenderer.material.backgroundAlpha = 0

  rtRenderer.material.physicalCamera.updateFrom(camera)

  /**
   * Controls
   */

  const controls = new OrbitControls(
    isOrtho ? orthoCameraMain : camera,
    renderer.domElement
  )
  controls.target.set(15, 0, 15)
  controls.update()

  controls.addEventListener('change', () => {
    console.log(orthoCameraMain.position, orthoCameraMain.zoom)
    rtRenderer.reset()
  })

  /*if (window.isFxPreview)*/ controls.enabled = false

  /**
   * Events
   */

  window.addEventListener('resize', onWindowResize)

  renderer.domElement.addEventListener('mouseup', onMouseUp)
  renderer.domElement.addEventListener('mousedown', onMouseDown)

  /* Material that will receive final image */

  const mat = new THREE.MeshBasicMaterial({
    map: rtRenderer.target.texture,
    transparent: true,
  })

  mat.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <clipping_planes_pars_fragment>',
      `#include <clipping_planes_pars_fragment>

      vec3 gammaCorrect(vec3 color, float gamma){
        return pow(color, vec3(1.0/gamma));
      }

      vec3 levelRange(vec3 color, float minInput, float maxInput){
          return min(max(color - vec3(minInput), vec3(0.0)) / (vec3(maxInput) - vec3(minInput)), vec3(1.0));
      }

      vec3 finalLevels(vec3 color, float minInput, float gamma, float maxInput){
          return gammaCorrect(levelRange(color, minInput, maxInput), gamma);
      }

      float contrast(float mValue, float mScale, float mMidPoint) {
        return clamp( (mValue - mMidPoint) * mScale + mMidPoint, 0.0, 1.0);
      }

      float contrast(float mValue, float mScale) {
        return contrast(mValue,  mScale, .5);
      }

      vec3 contrast(vec3 mValue, float mScale, float mMidPoint) {
        return vec3( contrast(mValue.r, mScale, mMidPoint), contrast(mValue.g, mScale, mMidPoint), contrast(mValue.b, mScale, mMidPoint) );
      }

      vec3 contrast(vec3 mValue, float mScale) {
        return contrast(mValue, mScale, .5);
      }

      float blendOverlay(float base, float blend) {
        return base<0.5?(2.0*base*blend):(1.0-2.0*(1.0-base)*(1.0-blend));
      }

      vec3 blendOverlay(vec3 base, vec3 blend) {
        return vec3(blendOverlay(base.r,blend.r),blendOverlay(base.g,blend.g),blendOverlay(base.b,blend.b));
      }

      vec3 blendOverlay(vec3 base, vec3 blend, float opacity) {
        return (blendOverlay(base, blend) * opacity + base * (1.0 - opacity));
      }`
    )

    if (isBAW) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>

        vec3 gray = mix(vec3(1.), vec3(dot(gl_FragColor.rgb, vec3(0.299, 0.587, 0.114))), .77);
        vec3 contrasted = contrast(gray, 1.4);

        gl_FragColor.rgb = contrasted;
        
        ${
          isOverlayed
            ? `gl_FragColor.rgb = blendOverlay(gl_FragColor.rgb, vec3(0.066666667, 0., 0.964705882), ${randomFloat(
                0.2,
                0.7,
                2
              )});`
            : ''
        }`
      )
    }
  }

  /**
   * Scene
   */

  const scene = new THREE.Scene()
  const sceneFs = new THREE.Scene()

  /* This plane is used to render the final image with composer */

  const meshPlane = new THREE.Mesh(new THREE.PlaneBufferGeometry(2, 2), mat)

  /**
   * Render Target
   * One RT per canvas textures
   */

  for (let i = 0; i < canvasTab.length; i++) {
    const sceneTarget = new THREE.Scene()
    const texTarget = new THREE.WebGLRenderTarget(
      canvasTab[i].width,
      canvasTab[i].height,
      {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.NearestFilter,
      }
    )

    targetTab.push({
      sceneTarget,
      texTarget,
    })
  }

  // const meshPlaneOverlay = new THREE.Mesh(
  //   new THREE.PlaneBufferGeometry(2, 2),
  //   new THREE.MeshBasicMaterial({
  //     transparent: true,
  //     map: targetTab[targetTab.length - 2].texTarget.texture,
  //   })
  // )

  // const meshPlaneBackground = new THREE.Mesh(
  //   new THREE.PlaneBufferGeometry(2, 2),
  //   new THREE.MeshBasicMaterial({
  //     // transparent: true,
  //     map: targetTab[targetTab.length - 1].texTarget.texture,
  //   })
  // )

  /**
   * Composer
   */

  const composer = new EffectComposer(renderer)
  const renderPass = new RenderPass(sceneFs, orthoCamera)
  const ditherPass = new ShaderPass(DitherShader)
  const scratchPass = new ShaderPass(ScratchShader)

  scratchPass.uniforms.uStrength = randomFloat(0.04, 0.1, 3)

  composer.addPass(renderPass)
  isDithering && composer.addPass(ditherPass)
  composer.addPass(scratchPass)

  /**
   * Main
   */

  canvasToTexture()
  setup()

  // sceneFs.add(meshPlaneBackground)
  sceneFs.add(meshPlane)
  // sceneFs.add(meshPlaneOverlay)

  /**
   * Events
   */

  document.addEventListener('keydown', (event) => {
    if (event.key.toLowerCase() === 's') {
      saveNextFrame = true
    } else if (event.key.toLowerCase() === 'p') {
      isRendering = !isRendering
    }
  })

  // const setListening = () => {
  //   document.addEventListener('keydown', (event) => {
  //     if (event.key.toLowerCase() === 'r') {
  //       isAuto = false
  //       draw()
  //     }
  //   })
  // }

  /**
   * Environment
   */

  const envPromise = new RGBELoader().loadAsync(envUrl)

  /**
   * Character
   */

  const charLoader = new GLTFLoader().loadAsync(characters)

  /**
   * BVH
   */

  const computeScene = () => {
    const generator = new PathTracingSceneWorker()
    return generator.generate(scene).then((result) => {
      const { bvh, textures, materials } = result

      bvhInfo = bvh
      const geometry = bvh.geometry
      const material = rtRenderer.material

      material.bvh.updateFrom(bvh)
      material.normalAttribute.updateFrom(geometry.attributes.normal)
      material.tangentAttribute.updateFrom(geometry.attributes.tangent)
      material.uvAttribute.updateFrom(geometry.attributes.uv)

      material.materialIndexAttribute.updateFrom(
        geometry.attributes.materialIndex
      )
      material.textures.setTextures(renderer, 2048, 2048, textures)
      material.materials.updateFrom(materials, textures)

      generator.dispose()
      // rtRenderer.reset()
    })
  }

  const res = await Promise.all([envPromise, computeScene(), charLoader])
  const envTex = res[0]

  envTex.mapping = THREE.EquirectangularReflectionMapping

  // scene.background = envTex
  scene.environment = envTex

  rtRenderer.material.environmentIntensity = envIntensity
  rtRenderer.material.environmentRotation.setFromMatrix4(
    new THREE.Matrix4().makeRotationY(randomInt(0, 180) * TO_RAD)
  )
  rtRenderer.material.envMapInfo.updateFrom(envTex)

  const humanMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
  })
  humanMat.roughness = 0.5

  const xMargin = (window.innerWidth - windowWidth) / 2
  const yMargin = (window.innerHeight - windowHeight) / 2

  for (let i = 0; i < randomInt(0, 1000); i++) {
    const posX = randomInt(xMargin, window.innerWidth - xMargin)
    const posY = randomInt(yMargin, window.innerHeight - yMargin)

    placeChars(posX, posY)
  }

  await computeScene()
  rtRenderer.reset()
  controls.update()
  setFocusPoint()

  renderer.domElement.removeEventListener('mouseup', onMouseUp)
  renderer.domElement.removeEventListener('mousedown', onMouseDown)

  draw()

  function setup() {
    // const material = new THREE.ShaderMaterial({
    //   uniforms: THREE.UniformsUtils.merge([
    //     THREE.UniformsLib['common'],
    //     THREE.UniformsLib['lights'],
    //     {
    //       map: {
    //         value: tex,
    //       },
    //     },
    //   ]),
    //   lights: true,
    //   vertexShader: vs,
    //   fragmentShader: fs,
    // })

    /* Prepare three gpu material */

    const matTab = targetTab.map((target, index) => {
      const mat = new THREE.MeshBasicMaterial({
        map: target.texTarget.texture,
      })

      /**
       * Possible settings
       * materialFinal.metalness = 0
       * materialFinal.transmission = 0
       * materialFinal.emissiveIntensity = 0.1
       * materialFinal.emissive = new THREE.Color(0xffffff)
       */

      if (index === targetTab.length - 1) {
        mat.metalness = 0.2
        mat.roughness = 0
        // mat.transmission = 10
        mat.emissiveIntensity = 50
        mat.emissiveMap = target.texTarget.texture
        // mat.emissive = new THREE.Color(0x000000)
      } else {
        mat.roughness = 0.5
      }

      return mat
      // const materialFinal: SimpleMat = {}
      // materialFinal.map =
      //   targetTab[randomInt(0, targetTab.length - 1)].texTarget.texture
      // materialFinal.color = new THREE.Color(0xffffff)
    })

    for (let i = 0; i < 30; i++) {
      for (let j = 0; j < 30; j++) {
        const materialFinal = matTab[randomInt(1, matTab.length - 2)]

        /* Roof material */
        const materialFinalB = matTab[randomInt(0, matTab.length - 2)]

        /* Neon material */
        const materialNeon = matTab[matTab.length - 1]

        /* Build the mesh */

        const computeX = randomInt(1, 2) + randomFloat(0, 0.1, 3)
        let computeY = randomNormal({ mean: 4, dev: 0.3 })
        const computeZ = randomInt(1, 2) + randomFloat(0, 0.1, 3)
        const x = i * 0.75
        const z = j * 0.75

        const offsetY = map(
          clamp(getPointsDist({ x: i, y: j }, { x: 15, y: 15 }), 0, 7),
          0,
          7,
          4,
          0
        )

        computeY -= offsetY

        const offsetYUp = map(
          clamp(getPointsDist({ x: i, y: j }, { x: 5, y: 5 }), 0, 4),
          0,
          4,
          3.5,
          0
        )

        computeY += offsetYUp

        // const offsetBorder = i === 0 || j === 0 || i === 29 || j === 29

        // computeY += offsetBorder ? 3 : 0

        const box = new THREE.BoxBufferGeometry(computeX, computeY, computeZ)
        const planeMesh = new THREE.Mesh(box, [
          materialFinal, // right
          materialFinal, // left
          materialFinalB, // top
          materialFinal, // bottom
          materialFinal, // front
          materialFinal, // back
        ])

        planeMesh.position.x = x
        planeMesh.position.y = computeY / 2
        planeMesh.position.z = z

        // planeMesh.geometry.computeBoundingBox()

        scene.add(planeMesh)

        if (planeMesh.position.y > 3.5) {
          planeMesh.material = [
            materialNeon,
            materialNeon,
            materialNeon,
            materialFinal,
            materialNeon,
            materialNeon,
          ]
          //   const NEON_WIDTH = 0.28
          //   const NEON_HEIGHT = 0.84
          //   const decalTop = 0
          //   const neonBox = new THREE.BoxBufferGeometry(
          //     NEON_WIDTH,
          //     NEON_HEIGHT,
          //     0.01
          //   )
          //   const neonMesh = new THREE.Mesh(neonBox, [
          //     materialFinal,
          //     materialFinal,
          //     materialFinal,
          //     materialFinal,
          //     materialNeon,
          //     materialNeon,
          //   ])

          //   neonMesh.position.y = computeY - NEON_HEIGHT / 2 - decalTop

          //   if (window.fxrand() > 0.5) {
          //     neonMesh.position.x = x - computeX / 2 - NEON_WIDTH / 2
          //     neonMesh.position.z = z
          //   } else {
          //     neonMesh.position.x = x
          //     neonMesh.position.z = z - computeZ / 2 - NEON_WIDTH / 2
          //     neonMesh.rotation.y = 90 * TO_RAD
          //   }

          //   neonMesh.geometry.computeBoundingBox()
          //   // console.log(neonMesh.geometry.boundingBox)
          //   scene.add(neonMesh)

          //   // const box = new THREE.BoxHelper(neonMesh, 0xffff00)
          //   // scene.add(box)

          //   // scene.traverse((child) => {
          //   // if (child.geometry?.boundingBox) {
          //   // console.log(child.geometry.boundingBox)
          //   // console.log(
          //   //   neonMesh.geometry.boundingBox.intersectsBox(
          //   //     child.geometry.boundingBox
          //   //   )
          //   // )
          //   // }
          //   // })
        }

        if (window.fxrand() < 0.6) {
          const decal = 0.04
          const size = randomFloat(computeZ * 0.25, computeZ * 0.75, 3)
          const boxUp = new THREE.BoxBufferGeometry(computeX, decal, size)

          // const merged = mergeBufferGeometries([box, boxUp])

          const planeMeshUp = new THREE.Mesh(boxUp, materialFinal)
          // const planeMeshUp = new Mesh(merged, materialFinal)

          planeMeshUp.position.x = x
          planeMeshUp.position.y = computeY / 2 + decal
          planeMeshUp.position.z = z - (computeZ - size) / 2

          scene.add(planeMeshUp)
        }
      }
    }
  }

  function draw() {
    const rafId = requestAnimationFrame(draw)

    if (isRendering) {
      // material.uniforms.uTime.value += 0.01

      // controls.update()
      rtRenderer.update()

      // renderer.autoClear = false
      composer.render()
      // renderer.render(scene, camera)
      // renderer.autoClear = true
      // console.log('render')
    }

    if (
      Math.floor(rtRenderer.samples) > 80 /*&& isAuto*/ &&
      window.isFxpreview
    ) {
      window.fxpreview()
      cancelAnimationFrame(rafId)

      // isCompletedRendering = true
      // setListening()
    } else if (
      Math.floor(rtRenderer.samples) > 100 &&
      isRendering &&
      isFirstStop
    ) {
      isRendering = isFirstStop = false
      console.log('paused')
    }

    if (Math.floor(rtRenderer.samples) > 1 && isPreloading) {
      isPreloading = false
      document.querySelector('.pre').classList.add('is-hidden')
    }

    if (saveNextFrame) {
      saveNextFrame = false
      const can = document.querySelector('#three-canvas') as HTMLCanvasElement
      const dataURL = can.toDataURL('image/png')
      const link = document.createElement('a')
      link.download = `maquette-${Date.now()}.png`
      link.href = dataURL
      link.click()
    }
  }

  function onWindowResize() {
    if (isPortrait) {
      windowHeight = window.innerHeight
      windowWidth = (windowHeight / 4) * 3
    } else {
      windowWidth = window.innerWidth
      windowHeight = (windowWidth / 4) * 3
    }

    const aspect = windowWidth / windowHeight

    camera.aspect = aspect
    camera.updateProjectionMatrix()

    const orthoHeight = orthoWidth / aspect
    orthoCameraMain.top = orthoHeight / 2
    orthoCameraMain.bottom = orthoHeight / -2
    orthoCameraMain.updateProjectionMatrix()

    // renderer.setPixelRatio(pixelRatio * params.resolutionScale)
    renderer.setSize(windowWidth, windowHeight, false)

    rtRenderer.target.setSize(
      windowWidth * params.resolutionScale * pixelRatio,
      windowHeight * params.resolutionScale * pixelRatio
    )

    rtRenderer.reset()

    // if (isCompletedRendering) {
    //   isCompleted = false
    //   if (!window.isFxpreview) draw()
    // }
  }

  function setFocusPoint() {
    const md = new Event('mousedown')
    const mu = new Event('mouseup')

    md.clientX = mu.clientX = windowWidth * 0.45
    md.clientY = mu.clientY = windowHeight * 0.65

    renderer.domElement.dispatchEvent(md)
    renderer.domElement.dispatchEvent(mu)
  }

  function onMouseDown(e: MouseEvent) {
    mouse.set(e.clientX, e.clientY)
  }

  function onMouseUp(e: MouseEvent) {
    const deltaMouse =
      Math.abs(mouse.x - e.clientX) + Math.abs(mouse.y - e.clientY)

    if (deltaMouse < 2) {
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(
        {
          x:
            ((e.clientX - (window.innerWidth - windowWidth) / 2) /
              windowWidth) *
              2 -
            1,
          y:
            -(
              (e.clientY - (window.innerHeight - windowHeight) / 2) /
              windowHeight
            ) *
              2 +
            1,
        },
        isOrtho ? orthoCameraMain : camera
      )

      const hit = bvhInfo.raycastFirst(raycaster.ray)

      if (hit) {
        camera.focusDistance = hit.distance - camera.near
        rtRenderer.material.physicalCamera.updateFrom(camera)

        rtRenderer.reset()
      }
    }
  }

  function placeChars(posX, posY) {
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(
      {
        x:
          ((posX - (window.innerWidth - windowWidth) / 2) / windowWidth) * 2 -
          1,
        y:
          -((posY - (window.innerHeight - windowHeight) / 2) / windowHeight) *
            2 +
          1,
      },
      isOrtho ? orthoCameraMain : camera
    )

    const hit = bvhInfo.raycastFirst(raycaster.ray)

    if (hit) {
      const totalHuman = res[2].scene.children.length
      const human = res[2].scene.children[randomInt(0, totalHuman - 1)].clone()
      human.material = humanMat

      human.scale.set(0.08, 0.08, 0.08)

      if (hit.face.normal.y !== 1) {
        const vA = new THREE.Vector3()
        const vB = new THREE.Vector3()
        const vC = new THREE.Vector3()
        const position = bvhInfo.geometry.attributes.position
        vA.fromBufferAttribute(position, hit.face.a)
        vB.fromBufferAttribute(position, hit.face.b)
        vC.fromBufferAttribute(position, hit.face.c)

        const minX = Math.min(vA.x, vC.x)
        const maxX = Math.max(vA.x, vC.x)
        const rdmX = randomFloat(minX, maxX, 2)
        const minZ = Math.min(vA.z, vC.z)
        const maxZ = Math.max(vA.z, vC.z)
        const rdmZ = randomFloat(minZ, maxZ, 2)

        human.position.set(rdmX, vA.y, rdmZ)
        human.rotation.y = Math.atan2(hit.face.normal.x, hit.face.normal.z)
      } else {
        human.position.set(hit.point.x, hit.point.y, hit.point.z)
        human.rotation.y = randomInt(0, 360) * TO_RAD
      }

      scene.add(human)
    }
  }
})()
